import { t } from 'i18next'
import moment from 'moment'
import { UseFormReturn } from 'react-hook-form'
import { CageConfig } from 'src/constant'
import { IPosition, IPositionType } from 'src/generated-types'
import { ItemIdentificationInput } from 'src/generated-types/item-identification'
import { IMainComponent } from 'src/generated-types/main-component'
import { getTranslatedText, setToMidnight } from 'src/utils'
import { MainComponentStatus, Months, ServiceStationType } from '../enums'
import { GlobalDocumentStatus } from '../generated-types/global-documents'
import { letterArray } from '../screens/localities/components/map/constraints'

export const getValueFromStringKey = (value, key) => {
  const keys = key.split('.')
  let valueReturn = value
  keys.forEach(element => {
    valueReturn = valueReturn[element]
  })
  return valueReturn ?? '-'
}

export const getServiceStationType = type => {
  switch (type) {
    case ServiceStationType.ServiceStation:
      return 'serviceStations'
    case ServiceStationType.Storage:
      return 'storages'
    case ServiceStationType.Service:
      return 'services'
  }
}

export function generateUUID() {
  let date = new Date().getTime() //Timestamp
  let currentDate = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0 //Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (character) {
    let randomValue = Math.random() * 16 //random number between 0 and 16
    if (date > 0) {
      //Use timestamp until depleted
      randomValue = (date + randomValue) % 16 | 0
      date = Math.floor(date / 16)
    } else {
      //Use microseconds since page-load if supported
      randomValue = (currentDate + randomValue) % 16 | 0
      currentDate = Math.floor(currentDate / 16)
    }
    return (character === 'x' ? randomValue : (randomValue & 0x3) | 0x8).toString(16)
  })
}

export const getCageName = (cageNumber: string, reference?: string) => {
  if (reference != null && reference.trim().length > 0) {
    return reference.trim()
  }
  const parsedCageNumber = parseInt(cageNumber)
  return parsedCageNumber >= CageConfig.OFFSET_START && parsedCageNumber < CageConfig.OFFSET_END
    ? String.fromCharCode(parsedCageNumber - CageConfig.OFFSET_START + CageConfig.ASCII_START_VALUE)
    : cageNumber
}

/** Returns the system default cage letter (A, B, C, …) without applying reference override. */
export const getCageGenericIdentification = (cageNumber?: string): string | undefined => {
  if (!cageNumber) return undefined
  const parsedCageNumber = parseInt(cageNumber)
  return parsedCageNumber >= CageConfig.OFFSET_START && parsedCageNumber < CageConfig.OFFSET_END
    ? String.fromCharCode(parsedCageNumber - CageConfig.OFFSET_START + CageConfig.ASCII_START_VALUE)
    : cageNumber
}

/** Cage label: reference if set, otherwise falls back to the generic identification. */
export const getCageDisplayLabel = (cageNumber?: string, reference?: string): string => {
  const ref = reference?.trim()
  if (ref) return ref
  return getCageGenericIdentification(cageNumber) ?? cageNumber?.trim() ?? ''
}

const ITEM_IDENTIFICATION_SEPARATOR = ' | '

/** Formats: Item | Generic ID | Reference | Note | Item ID (only non-empty parts are included). */
export const formatItemIdentification = (input: ItemIdentificationInput): string => {
  const parts: string[] = []

  const itemType = input.itemType?.trim()
  if (itemType) parts.push(itemType)

  const genericId = input.positionName ? getCageGenericIdentification(input.positionName) : undefined
  if (genericId) parts.push(genericId)

  const reference = input.mainComponentReference?.trim() || input.positionReference?.trim()
  if (reference) parts.push(reference)

  const note = input.positionNote?.trim()
  if (note) parts.push(note)

  const itemId = input.serialNumber?.trim()
  if (itemId) parts.push(itemId)

  return parts.length > 0 ? parts.join(ITEM_IDENTIFICATION_SEPARATOR) : '-'
}

export const formatMainComponentIdentification = (
  equipment: Pick<IMainComponent, 'serialNumber' | 'type' | 'position'> & {
    reference?: string
    mainComponentTypeName?: string
  },
  options?: { itemType?: string },
): string => {
  const itemType =
    options?.itemType ??
    (equipment.type?.name ? getTranslatedText(equipment.type.name) : undefined) ??
    (equipment.mainComponentTypeName ? getTranslatedText(equipment.mainComponentTypeName) : undefined)

  return formatItemIdentification({
    itemType,
    positionName: equipment.position?.name,
    positionReference: equipment.position?.reference,
    positionNote: equipment.position?.note,
    mainComponentReference: equipment.reference,
    serialNumber: equipment.serialNumber,
  })
}

export const getPositionName = (nameOrPosition: IPosition, refWithName?: boolean) => {
  if (nameOrPosition != null && typeof nameOrPosition === 'object') {
    const position = nameOrPosition
    if (position.type === IPositionType.Cage) {
      return getCageName(position.name, position.reference)
    }
    return refWithName && position.reference
      ? `${position.reference?.trim()} (${position.name?.trim()})`
      : position.reference?.trim() || position.name?.trim() || ''
  } else {
    return ''
  }
}

export const getCageLetter = (cageNumber: string) => {
  return letterArray[parseInt(cageNumber) - CageConfig.OFFSET_START] ?? 0
}

export const getLocalDateTime = (date: Date | undefined) => {
  if (!date) return undefined

  const localISO = moment.utc(date).local().toISOString()
  return new Date(localISO)
}

export const getLocalFormattedDateTimeFromUTCString = (date: string, format = 'YYYY-MM-DD') => {
  return moment.utc(date).local().format(format)
}

export const formatNumber = (num, padStartCount = 0) => {
  let returnNumber = num
  if (num >= 1e6) {
    returnNumber = (num / 1e6).toFixed(1) + 'M'
  } else if (num >= 1e3) {
    returnNumber = (num / 1e3).toFixed(1) + 'K'
  }
  return returnNumber.toString().padStart(padStartCount, '0')
}

export const addCurrentTimeToDate = date => {
  if (!date) return undefined

  const today = new Date()
  const dateTime = new Date(date)
  dateTime.setHours(today.getHours(), today.getMinutes(), today.getSeconds())

  return getUTCDate(dateTime)
}

export const getUTCDate = (date: Date) => {
  if (!date) return undefined

  // Get the year, month, and day from the local date
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  // Create a new date object in the local timezone
  const localDate = new Date(year, month, day)

  // Get the UTC date corresponding to the local date
  const utcDate = new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()))

  return utcDate
}

export const convertFirstLetterToUpperCase = (word: string) => {
  return word[0].toUpperCase() + word.substring(1)
}

export const getGlobalDocumentStatus = (status: GlobalDocumentStatus) => {
  switch (status) {
    case GlobalDocumentStatus.Approved:
      return 'approved'
    case GlobalDocumentStatus.Withdrawn:
      return 'withdrawn'
    case GlobalDocumentStatus.SaveDraft:
      return 'draft'
    default:
      return 'new'
  }
}

export const getYearAndMonth = (year?: number, month?: number) => {
  if (year != undefined && month != undefined) {
    return `${year}-${Months[month]}`
  }
  if (year) {
    return `${year}`
  }
  return '-'
}

export const dateWithoutTimeConvert = (dateString: string | Date) => {
  const date = new Date(dateString)
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  return date
}

export const sumTwoNumbers = (num1, num2) => {
  return (num1 ?? 0) + (num2 ?? 0)
}

// Check if the string is only-symbols-string
export const checkAlphaNumerics = value => {
  const hasAlphanumerics = /[a-zA-Z0-9]/.test(value)
  return hasAlphanumerics
}

export const decodeFrequencyInstructionHTML = instruction => {
  const instructiontextArea = document.createElement('textarea')
  instructiontextArea.innerHTML = instruction
  const doc = new DOMParser().parseFromString(instructiontextArea.value, 'text/html')
  return doc.body.textContent || ''
}

export const getMooringName = (mooringname: string) => {
  const nameArray = mooringname.split('|')
  return `${t(nameArray[0].trim(), { ns: 'mooring' })} ${
    nameArray.length > 1 && nameArray[1].trim().length > 0 ? ' | ' + nameArray[1] : ''
  }`
}

export const getMainComponentName = (name: string) => {
  if (name != null) {
    const nameArray = name.split('|')
    // to support old data, display component name that was previously logged
    if (nameArray[0].trim()[0] != '{') return nameArray[0].trim()

    // new data contains 'type object | serial number' format string
    return getTranslatedText(nameArray[0].trim()) + ' | ' + (nameArray.length > 1 ? nameArray[1].trim() : '')
  } else return '-'
}

export const getMainComponentStatus = (mainComponent: IMainComponent) => {
  if (mainComponent.isDeleted) {
    return 'discarded'
  }
  if (mainComponent.status == MainComponentStatus.InStock && mainComponent.storageName) {
    return 'inStorage'
  } else if (mainComponent.status == MainComponentStatus.Outgoing) {
    return 'SentToLocality'
  }

  return MainComponentStatus[mainComponent.status]
}

export const getPlacementArray = (placementData: string) => {
  const jsonPlacement = JSON.parse(placementData)
  delete jsonPlacement.positionDrawingNumber

  // Extract positionType and positionReference before iterating — used to label/name the
  // position entry but should not appear as their own breadcrumb segments.
  const positionType: string | undefined = jsonPlacement.positionType
  const positionReference: string | undefined = jsonPlacement.positionReference
  delete jsonPlacement.positionType
  delete jsonPlacement.positionReference

  const placement = Object.entries(jsonPlacement).map(([key, value]) => {
    if (key === 'locality' && value != null) {
      return [key, getMooringName(value as string)]
    }
    if (key === 'mainComponent' && value != null) {
      return [key, getMainComponentName(value as string)]
    }
    if (key === 'position' && value != null && positionType) {
      const typeLabel = t(positionType, { ns: 'position' })
      const positionLabel =
        positionType === 'Cage'
          ? getCageName(value as string, positionReference)
          : positionReference ?? (value as string)
      return [key, `${typeLabel} (${positionLabel})`]
    }
    return [key, value]
  })
  const placementArray = placement
    .filter(([, item]) => item)
    .map(([, option]) => option)
    .reverse()

  return placementArray.join(' <- ')
}

export const covertToDateType = (value: unknown): Date | null => {
  if (!value) return null
  return value instanceof Date ? value : new Date(value as string)
}

export const convertToPascalCase = (str: string) => {
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const sortCertificates = (items: any[] = []) => {
  const validNonExpired: any[] = []
  const validExpired: any[] = []
  const invalid: { cert: any; originalIndex: number }[] = []
  const now = setToMidnight(new Date()).getTime()

  items.forEach((cert, idx) => {
    if (cert.isValid === true) {
      const validUntil = cert?.validUntil?.value ?? cert?.validUntil
      const expiryDate = validUntil ? new Date(validUntil).getTime() : Number.MAX_SAFE_INTEGER

      if (expiryDate >= now) {
        validNonExpired.push(cert)
      } else {
        validExpired.push(cert)
      }
    } else {
      invalid.push({ cert, originalIndex: idx })
    }
  })

  validNonExpired.sort((a, b) => {
    const aRaw = a?.validUntil
    const bRaw = b?.validUntil
    const aDateVal = aRaw?.value ?? aRaw ? new Date(aRaw?.value ?? aRaw).getTime() : Number.MAX_SAFE_INTEGER
    const bDateVal = bRaw?.value ?? bRaw ? new Date(bRaw?.value ?? bRaw).getTime() : Number.MAX_SAFE_INTEGER
    return aDateVal - bDateVal
  })

  validExpired.sort((a, b) => {
    const aRaw = a?.validUntil
    const bRaw = b?.validUntil
    const aDateVal = aRaw?.value ?? aRaw ? new Date(aRaw?.value ?? aRaw).getTime() : Number.MAX_SAFE_INTEGER
    const bDateVal = bRaw?.value ?? bRaw ? new Date(bRaw?.value ?? bRaw).getTime() : Number.MAX_SAFE_INTEGER
    return aDateVal - bDateVal
  })

  const invalidCerts = invalid.sort((a, b) => a.originalIndex - b.originalIndex).map(e => e.cert)

  return [...validNonExpired, ...validExpired, ...invalidCerts]
}

interface PositionInstallationForm {
  selectedPositionId?: EntityId | null | ''
  installationDate?: Date | string | null
}

export function validatePositionAndInstallation(
  form: PositionInstallationForm,
  translate: (key: string, options?: { ns: string }) => string,
  formContext?: UseFormReturn<any>,
) {
  const hasPosition =
    form?.selectedPositionId !== undefined &&
    form?.selectedPositionId !== null &&
    form.selectedPositionId !== 0 &&
    form.selectedPositionId !== ''
  const hasDate = form?.installationDate != null && form.installationDate !== ''
  if (hasPosition && !hasDate) {
    if (formContext && typeof formContext.setError === 'function') {
      formContext.setError('installationDate', {
        type: 'manual',
        message: translate('required', { ns: 'common' }),
      })
    }
    return false
  }
  if (hasDate && !hasPosition) {
    if (formContext && typeof formContext.setError === 'function') {
      formContext.setError('selectedPositionId', {
        type: 'manual',
        message: translate('required', { ns: 'common' }),
      })
    }
    return false
  }
  return true
}

export const toNumberOrNull = (value: unknown): number | null => {
  if (value === '' || value === undefined || value === null) return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}
